import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone,
  Calendar,
  Globe,
  MapPin,
  Sparkles,
  Layers,
  Shield,
  Clock,
  Zap,
  Target,
  Users,
  CheckCircle2,
  ArrowRight,
  Star,
  BarChart3,
  Wrench,
  ThermometerSun,
  Droplets,
  MessageSquare,
  DollarSign,
  Activity,
  Award,
  FileText,
  Headphones,
  PhoneIncoming,
  Mic,
  Orbit,
  Waypoints,
  Brain,
  BrainCircuit,
  Volume2,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/use-page-meta";
import { trackEvent } from "@/lib/posthog";
import { JsonLd, organizationSchema, platformSchema } from "@/components/seo/JsonLd";
import mynaLogo from "@/assets/myna-logo.svg";
import aiIcon from "@/assets/ai-icon.svg";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";

/**
 * MYNA HOMEPAGE - Intelligent Business Systems Platform
 * Washed charcoal aesthetic, sharp corners, muted lime/teal
 *
 * Updated March 2026:
 * - 6-pillar platform messaging (AI Phone, Website, Booking, Reputation, SEO, Dashboard)
 * - Multi-vertical positioning (HVAC, Plumbing, Mortgage Brokers, more)
 * - Tier structure: Starter ($649), Growth ($899), Dominator ($1,199)
 */

export default function NewHome() {
  usePageMeta({
    title: "Intelligent Business Systems for Service Companies",
    description: "MYNA AI powers your business with AI phone systems, professional websites, smart booking, reputation management, and local SEO — all in one platform.",
    path: "/",
  });

  return (
    <div className="min-h-screen bg-[var(--m-bg)] text-[var(--m-text-primary)] overflow-x-hidden">
      <JsonLd data={organizationSchema} />
      <JsonLd data={platformSchema} />
      {/* Gradient wash background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--m-bg-gradient-from)] via-[var(--m-bg-gradient-via)] to-[var(--m-bg-gradient-to)]" />
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px]"
          style={{
            background: "radial-gradient(circle, rgba(132,204,22,0.025) 0%, transparent 70%)",
            filter: "blur(140px)",
          }}
        />
      </div>

      <Navbar />

      <main id="main-content" className="relative z-10 pt-16 md:pt-20 lg:pt-24">
        {/* Hero Section with floating activity visualization */}
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 pt-10 pb-12 md:pt-20 md:pb-20 relative min-h-0 md:min-h-[600px]">
          {/* Voice AI Orb - Premium conic-gradient orb with glass highlights */}
          <div className="absolute hidden lg:flex flex-col items-center" role="presentation" aria-hidden="true" style={{ right: "140px", top: "60px", zIndex: 50 }}>
            {/* Chat bubble above orb */}
            <motion.div
              className="flex items-center gap-2.5 px-5 py-3 mb-6 backdrop-blur-sm border bg-gradient-to-br from-[var(--m-bg-gradient-from)] to-[var(--m-card)] border-[var(--m-ring-border)]"
              style={{
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5), 0 0 24px rgba(132, 204, 22, 0.12)",
                willChange: 'transform, opacity',
              }}
              animate={{
                opacity: [0, 0, 1, 1, 1, 0, 0],
                y: [8, 8, 0, 0, 0, -4, 8],
                scale: [0.92, 0.92, 1, 1, 1, 0.96, 0.92],
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                times: [0, 0.12, 0.22, 0.55, 0.78, 0.88, 1],
                ease: "easeInOut",
              }}
            >
              <span
                className="text-sm font-semibold tracking-wide"
                style={{
                  background: "linear-gradient(90deg, #84cc16 0%, #a3e635 50%, #14b8a6 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                6 systems. One platform.
              </span>
            </motion.div>

            {/* Voice orb — Milka-inspired conic gradient design */}
            <motion.div
              className="relative"
              style={{ willChange: 'transform', width: 120, height: 120 }}
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
            >
              {/* Dashed rotating ring 1 */}
              <motion.div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: "140%",
                  height: "140%",
                  top: "-20%",
                  left: "-20%",
                  border: "1px dashed rgba(204, 255, 81, 0.25)",
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              />
              {/* Dashed rotating ring 2 */}
              <motion.div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: "160%",
                  height: "160%",
                  top: "-30%",
                  left: "-30%",
                  border: "1px dashed rgba(244, 244, 244, 0.12)",
                }}
                animate={{ rotate: -360 }}
                transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
              />

              {/* Ambient glow behind orb */}
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: 200,
                  height: 200,
                  top: -40,
                  left: -40,
                  background: "radial-gradient(circle, rgba(204, 255, 81, 0.15) 0%, transparent 70%)",
                  filter: "blur(20px)",
                }}
              />

              {/* Main orb body — conic gradient */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: "conic-gradient(from 0deg, #ccff51 0deg, #2dd4bf 40deg, #ccff51 70deg, #ccff51 110deg, #f4f4f4 140deg, #ccff51 170deg, #ccff51 210deg, #2dd4bf 240deg, #ccff51 270deg, #ccff51 310deg, #f4f4f4 335deg, #ccff51 360deg)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  willChange: "transform",
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              />

              {/* Breathing glow shadow */}
              <motion.div
                className="absolute inset-0 rounded-full pointer-events-none"
                animate={{
                  boxShadow: [
                    "0 0 35px rgba(204, 255, 81, 0.3), 0 0 70px rgba(244, 244, 244, 0.1), inset 0 0 25px rgba(255, 255, 255, 0.06)",
                    "0 0 55px rgba(204, 255, 81, 0.5), 0 0 95px rgba(244, 244, 244, 0.2), inset 0 0 35px rgba(255, 255, 255, 0.12)",
                    "0 0 35px rgba(204, 255, 81, 0.3), 0 0 70px rgba(244, 244, 244, 0.1), inset 0 0 25px rgba(255, 255, 255, 0.06)",
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Glass highlight overlay — wandering specular */}
              <motion.div
                className="absolute rounded-full pointer-events-none"
                style={{ inset: 3, zIndex: 2 }}
                animate={{
                  background: [
                    "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.22), transparent 45%), radial-gradient(circle at 70% 75%, rgba(204,255,81,0.06), transparent 50%)",
                    "radial-gradient(circle at 60% 20%, rgba(255,255,255,0.28), transparent 45%), radial-gradient(circle at 30% 80%, rgba(244,244,244,0.08), transparent 50%)",
                    "radial-gradient(circle at 70% 50%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 20% 40%, rgba(204,255,81,0.08), transparent 50%)",
                    "radial-gradient(circle at 40% 70%, rgba(255,255,255,0.24), transparent 45%), radial-gradient(circle at 65% 25%, rgba(244,244,244,0.06), transparent 50%)",
                    "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.22), transparent 45%), radial-gradient(circle at 70% 75%, rgba(204,255,81,0.06), transparent 50%)",
                  ],
                }}
                transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Pulse ripples flowing outward */}
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={`pulse-${i}`}
                  className="absolute rounded-full pointer-events-none"
                  style={{
                    inset: 0,
                    border: "2px solid rgba(45, 212, 191, 0.35)",
                  }}
                  animate={{
                    scale: [1, 2.5],
                    opacity: [0.5, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    delay: i * 2,
                    ease: "easeOut",
                  }}
                />
              ))}

              {/* Center icon */}
              <div
                className="absolute inset-0 flex items-center justify-center rounded-full"
                style={{ zIndex: 3 }}
              >
                <img src={aiIcon} alt="" className="w-16 h-16 drop-shadow-lg" style={{ filter: "brightness(0) invert(1) brightness(0.25)" }} />
              </div>
            </motion.div>
          </div>


          <div className="grid lg:grid-cols-[3fr_2fr] gap-8 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              style={{ willChange: 'transform, opacity' }}
            >
              {/* Mobile Voice Orb - Simplified conic gradient */}
              <div className="lg:hidden flex justify-center mb-8">
                <div className="relative w-20 h-20">
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: "conic-gradient(from 0deg, #ccff51 0deg, #2dd4bf 40deg, #ccff51 70deg, #ccff51 110deg, #f4f4f4 140deg, #ccff51 170deg, #ccff51 210deg, #2dd4bf 240deg, #ccff51 270deg, #ccff51 310deg, #f4f4f4 335deg, #ccff51 360deg)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      boxShadow: "0 0 35px rgba(204, 255, 81, 0.4), 0 0 60px rgba(244, 244, 244, 0.15)",
                    }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  />
                  <div className="absolute inset-[3px] rounded-full" style={{ background: "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.2), transparent 50%)" }} />
                  <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 3 }}>
                    <img src={aiIcon} alt="" className="w-14 h-14 drop-shadow-lg" style={{ filter: "brightness(0) invert(1) brightness(0.25)" }} />
                  </div>
                </div>
              </div>

              {/* Eyebrow badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--m-badge-bg)] border border-[var(--m-ring-border)] mb-8">
                <Sparkles className="w-4 h-4 text-[var(--m-lime)]" />
                <span className="text-xs font-bold text-[var(--m-lime)] tracking-wider uppercase">Intelligent Business Systems</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black leading-[1.02] mb-6 md:mb-8 tracking-tight">
                <span className="text-[var(--m-heading)]">Your Business.</span>
                <br />
                <span
                  className="bg-gradient-to-r from-lime-400 to-teal-400 bg-clip-text text-transparent"
                  style={{
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Supercharged.
                </span>
              </h1>

              <div className="space-y-4 mb-10">
                <p className="text-base sm:text-lg md:text-xl text-[var(--m-body)] leading-relaxed">
                  AI phone system, professional website, smart booking, reputation intelligence, and local SEO —
                  everything your service business needs to capture leads, book jobs, and dominate your market.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 md:mb-12">
                <Link to="/request-demo" onClick={() => trackEvent('cta_click', { cta: 'book_demo', location: 'hero', page: 'home' })}>
                  <Button className="px-6 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-[#f4f4f4] to-lime-400 text-[#292929] font-black text-base sm:text-lg hover:opacity-90 transition-all inline-flex items-center justify-center gap-2 shadow-2xl shadow-lime-500/25 hover:scale-105 focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--m-bg)]">
                    Book a Demo
                    <Calendar className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing" onClick={() => trackEvent('cta_click', { cta: 'view_pricing', location: 'hero', page: 'home' })}>
                  <Button variant="outline" className="px-6 sm:px-10 py-4 sm:py-5 bg-transparent border-2 border-[var(--m-border-strong)] text-[var(--m-heading)] font-bold hover:bg-[var(--m-card)]/10 hover:border-[var(--m-border-strong)] transition-all focus-visible:ring-2 focus-visible:ring-[var(--m-lime)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--m-bg)]">
                    See Pricing & ROI
                  </Button>
                </Link>
              </div>

              {/* Quick stats - Platform-level value props */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5">
                {[
                  { value: "6", label: "Pillars", icon: Layers, color: "lime" },
                  { value: "1", label: "Platform", icon: Target, color: "teal" },
                  { value: "24/7", label: "Active", icon: Clock, color: "lime" },
                  { value: "Zero", label: "Missed Leads", icon: CheckCircle2, color: "teal" },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className={`p-3 sm:p-5 border-2 transition-all active:scale-95 md:hover:scale-105 md:hover:-translate-y-1 shadow-lg ${
                      stat.color === "lime"
                        ? "bg-gradient-to-br from-lime-500/10 to-lime-500/5 border-lime-500/30 md:hover:shadow-lime-500/20"
                        : "bg-gradient-to-br from-teal-500/10 to-teal-500/5 border-teal-500/30 md:hover:shadow-teal-500/20"
                    }`}
                  >
                    <stat.icon
                      className={`w-5 h-5 mb-3 ${stat.color === "lime" ? "text-lime-400" : "text-teal-400"}`}
                      strokeWidth={2}
                    />
                    <div
                      className={`text-2xl sm:text-4xl font-black mb-1 ${
                        stat.color === "lime" ? "text-lime-400" : "text-teal-400"
                      }`}
                    >
                      {stat.value}
                    </div>
                    <div className="text-xs text-[var(--m-muted)] font-medium tracking-wide uppercase">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Dashboard preview with floating activity cards */}
            <div className="relative">
              {/* Floating activity cards - one per pillar */}
              {[
                {
                  icon: Phone,
                  text: "Call Answered",
                  subtext: "< 2 seconds",
                  left: "-150px",
                  top: "20px",
                  delay: 0,
                  color: "lime",
                },
                {
                  icon: Globe,
                  text: "Website Visit",
                  subtext: "→ Booked",
                  left: "-145px",
                  top: "-65px",
                  delay: 8,
                  color: "teal",
                },
                {
                  icon: Calendar,
                  text: "Appointment",
                  subtext: "Confirmed",
                  left: "15px",
                  top: "-65px",
                  delay: 2,
                  color: "teal",
                },
                {
                  icon: Star,
                  text: "5-Star Review",
                  subtext: "Published",
                  right: "-120px",
                  top: "-65px",
                  delay: 10,
                  color: "lime",
                },
                {
                  icon: MapPin,
                  text: "Google 3-Pack",
                  subtext: "Ranked #1",
                  left: "15px",
                  bottom: "-65px",
                  delay: 4,
                  color: "lime",
                },
                {
                  icon: BarChart3,
                  text: "Revenue",
                  subtext: "+32% MTD",
                  left: "180px",
                  top: "-65px",
                  delay: 12,
                  color: "teal",
                },
              ].map((activity, i) => (
                <motion.div
                  key={i}
                  className={`absolute hidden xl:flex items-center gap-2.5 px-3 py-2 backdrop-blur-sm border ${
                    activity.color === "lime"
                      ? "bg-lime-500/5 border-lime-500/30"
                      : "bg-teal-500/5 border-teal-500/30"
                  }`}
                  style={{
                    left: activity.left,
                    right: activity.right,
                    top: activity.top,
                    bottom: activity.bottom,
                    boxShadow:
                      activity.color === "lime"
                        ? "0 8px 24px rgba(132, 204, 22, 0.08)"
                        : "0 8px 24px rgba(20, 184, 166, 0.08)",
                    willChange: 'transform, opacity',
                  }}
                  initial={{ opacity: 0, scale: 0.8, y: 10 }}
                  animate={{
                    opacity: [0, 0, 1, 1, 1, 0, 0],
                    scale: [0.8, 0.8, 1, 1, 1, 0.95, 0.8],
                    y: [10, 10, 0, -3, 3, -5, 10],
                  }}
                  transition={{
                    duration: 14,
                    repeat: Infinity,
                    delay: activity.delay,
                    ease: "easeInOut",
                    times: [0, 0.07, 0.14, 0.5, 0.86, 0.93, 1],
                  }}
                >
                  <div
                    className={`w-7 h-7 flex items-center justify-center border ${
                      activity.color === "lime"
                        ? "bg-lime-500/15 border-lime-500/40"
                        : "bg-teal-500/15 border-teal-500/40"
                    }`}
                  >
                    <activity.icon
                      className={`w-3.5 h-3.5 ${activity.color === "lime" ? "text-lime-400" : "text-teal-400"}`}
                      strokeWidth={2}
                    />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[var(--m-heading)]">{activity.text}</p>
                    <p className="text-[11px] text-[var(--m-subtle)]">{activity.subtext}</p>
                  </div>
                </motion.div>
              ))}

              <motion.div
                className="bg-gradient-to-br from-[var(--m-card)] via-[var(--m-card-alt)] to-[var(--m-card)] border-2 border-[var(--m-border-strong)] p-6 shadow-2xl relative"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                style={{
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                  willChange: 'transform, opacity',
                }}
              >
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-lime-500/[0.02] to-teal-500/[0.02] pointer-events-none" />

              <section className="relative z-10" aria-label="Business command center preview showing cross-platform metrics">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-base font-semibold text-[var(--m-heading)] mb-0.5">Business Command Center</h3>
                    <p className="text-[10px] text-[var(--m-subtle)]">Cross-platform metrics</p>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500/10 border border-teal-500/30">
                    <Activity className="w-2.5 h-2.5 text-teal-400" />
                    <span className="text-[10px] text-teal-400 font-bold">ACTIVE</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Cross-platform metrics */}
                  <div className="flex items-center justify-between p-3 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-lime-400" />
                      <span className="text-xs text-[var(--m-body)]">Calls Answered</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--m-heading)]">100%</span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-teal-400" />
                      <span className="text-xs text-[var(--m-body)]">Website Leads</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--m-heading)]">12 <span className="text-sm text-[var(--m-subtle)]">this wk</span></span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[var(--m-elevated)] border border-[var(--m-border)]">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-lime-400" />
                      <span className="text-xs text-[var(--m-body)]">Review Score</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--m-heading)]">4.9<span className="text-lime-400">★</span></span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-teal-500/5 border border-teal-500/20">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-teal-400" />
                      <span className="text-xs text-[var(--m-body)]">Local Rank</span>
                    </div>
                    <span className="text-2xl font-bold text-teal-400">#1</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-[var(--m-border)]">
                  <div className="flex items-center gap-1.5 text-[10px] text-[var(--m-subtle)]">
                    <CheckCircle2 className="w-2.5 h-2.5 text-teal-400" />
                    All systems operational
                  </div>
                </div>
              </section>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Geometric divider */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="border-t border-[var(--m-border-strong)]" />
        </div>

        {/* Features Section - 12 REAL features from FEATURE_PRIORITY_MATRIX.md */}
        <section id="platform" className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-12 md:py-20">
          <div className="mb-10 md:mb-16">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-8 md:mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--m-badge-bg)] border border-[var(--m-ring-border)] mb-6">
                  <Target className="w-4 h-4 text-[var(--m-lime)]" />
                  <span className="text-xs font-bold text-[var(--m-lime)] tracking-wider uppercase">6 Pillars. One Platform.</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-5 text-[var(--m-heading)] leading-tight">
                  Intelligent{" "}
                  <span className="bg-gradient-to-r from-lime-400 to-teal-400 bg-clip-text text-transparent">
                    Business
                  </span>{" "}
                  Systems
                </h2>
                <p className="text-base md:text-lg text-[var(--m-muted)] leading-relaxed">
                  AI phone, professional website, smart booking, reputation intelligence, local SEO,
                  and a command center dashboard — everything to capture leads and grow revenue.
                </p>
              </motion.div>

              {/* Right side: Feature count highlight */}
              <motion.div
                className="flex justify-end"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: "6", label: "Pillars", color: "lime" },
                    { value: "1", label: "Platform", color: "teal" },
                    { value: "0", label: "Extra Costs", color: "lime" },
                  ].map((stat, i) => (
                    <div
                      key={i}
                      className={`p-5 text-center border-2 ${
                        stat.color === "lime"
                          ? "bg-lime-500/5 border-lime-500/20"
                          : "bg-teal-500/5 border-teal-500/20"
                      }`}
                    >
                      <div className={`text-2xl sm:text-4xl font-black mb-1 ${
                        stat.color === "lime" ? "text-lime-400" : "text-teal-400"
                      }`}>
                        {stat.value}
                      </div>
                      <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>

          {/* 9 features - Launch day features only, professional copywriting */}
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Phone,
                title: "Never Miss Another Late-Night Emergency Call",
                desc: "Your AI receptionist answers in 2 rings—24/7, even at 2 AM. Qualifies the caller, checks your calendar, books the appointment, and sends confirmation. Sounds exactly like your best office staff, never takes a day off.",
                metrics: "<2 ring pickup",
              },
              {
                icon: Calendar,
                title: "Every Call Becomes a Booked Appointment",
                desc: "Qualified callers get appointments automatically—synced to Google Calendar, assigned to the right tech based on location and skills, confirmed with the customer. Your phone rings, revenue gets scheduled.",
                metrics: "Zero double-bookings",
              },
              {
                icon: Clock,
                title: "Stop the 'Where's My Tech?' Calls",
                desc: "When your tech marks 'en route,' the system calculates accurate arrival time using real-time traffic data and automatically texts the customer with tech name and ETA. Professional experience that reduces status calls and keeps everyone informed.",
                metrics: "Fewer calls",
              },
              {
                icon: Wrench,
                title: "Professional Website That Converts",
                desc: "Conversion-optimized, mobile-responsive website that turns visitors into booked appointments. Service pages, online booking, review widgets, and SEO-ready — all managed for you.",
                metrics: "Conversion-built",
              },
              {
                icon: Star,
                title: "Reputation Intelligence",
                desc: "AI-powered review monitoring, automated responses, and testimonial graphics that build trust. Turn happy customers into 5-star reviews and new leads automatically.",
                metrics: "Review growth",
              },
              {
                icon: Users,
                title: "Google 3-Pack Dominance",
                desc: "Complete local SEO system with geo-grid tracking, GBP management, weather-triggered posts, and competitor monitoring. Own the top 3 spots in local search.",
                metrics: "Top 3 rankings",
              },
              {
                icon: BarChart3,
                title: "Business Command Center",
                desc: "Real-time dashboard with call analytics, booking trends, review performance, and ROI tracking. See your entire business on one screen — from your phone or desktop.",
                metrics: "Real-time updates",
              },
              {
                icon: Shield,
                title: "Secure North American Data",
                desc: "Your business data stays in North America. We're committed to data sovereignty, privacy, and compliance with US & Canadian regulations. Built for North American businesses.",
                metrics: "100% North American",
              },
              {
                icon: MessageSquare,
                title: "Industry-Trained AI",
                desc: "Pre-trained on service industry terminology, common customer scenarios, and professional language. Works for HVAC, plumbing, electrical, mortgage brokers, and more.",
                metrics: "Multi-industry",
              },
            ].map((feature, i) => {
              const isLime = i % 2 === 0; // Alternating pattern
              return (
                <motion.div
                  key={i}
                  className={`p-6 bg-[var(--m-card)] border-2 transition-all hover:-translate-y-1 group relative overflow-hidden ${
                    isLime
                      ? "shadow-lg shadow-lime-500/10 hover:shadow-xl hover:shadow-lime-500/20"
                      : "shadow-lg shadow-teal-500/10 hover:shadow-xl hover:shadow-teal-500/20"
                  }`}
                  style={{
                    borderImage: isLime
                      ? "linear-gradient(135deg, rgba(132,204,22,0.4), rgba(132,204,22,0.2)) 1"
                      : "linear-gradient(135deg, rgba(20,184,166,0.4), rgba(20,184,166,0.2)) 1",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                >
                  {/* Gradient border overlay */}
                  <div
                    className={`absolute inset-0 pointer-events-none ${
                      isLime ? "border-2 border-lime-500/30" : "border-2 border-teal-500/30"
                    }`}
                  />

                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className={`p-2.5 border-2 ${
                          isLime ? "bg-lime-500/15 border-lime-500/40" : "bg-teal-500/15 border-teal-500/40"
                        }`}
                      >
                        <feature.icon
                          className={`w-5 h-5 ${
                            isLime ? "text-lime-400" : "text-teal-400"
                          } group-hover:scale-110 transition-transform`}
                          strokeWidth={2}
                        />
                      </div>
                      <span className={`text-xs font-bold ${isLime ? "text-lime-400" : "text-teal-400"}`}>
                        {feature.metrics}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-[var(--m-heading)] mb-2">{feature.title}</h3>
                    <p className="text-sm text-[var(--m-muted)] leading-relaxed">{feature.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Social Proof Banner */}
        <section className="bg-[var(--m-deep)] py-10 md:py-16 border-y border-[var(--m-border)]">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {[
                {
                  quote: "Finally, I can focus on the job without worrying about missing calls.",
                  author: "HVAC Business Owner",
                  location: "Houston, TX",
                  icon: ThermometerSun,
                },
                {
                  quote: "The AI sounds so natural, customers don't even realize it's automated.",
                  author: "Plumbing Contractor",
                  location: "Phoenix, AZ",
                  icon: Droplets,
                },
                {
                  quote: "Setup took less than an hour. We were live the next day.",
                  author: "Service Manager",
                  location: "Chicago, IL",
                  icon: Zap,
                },
              ].map((testimonial, i) => (
                <motion.div
                  key={i}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  {/* Quote mark */}
                  <div className="text-6xl font-serif text-lime-500/20 absolute -top-4 -left-2 hidden sm:block leading-none">"</div>
                  <div className="relative z-10">
                    <p className="text-[var(--m-body)] text-base md:text-lg leading-relaxed mb-4 italic">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center border ${
                        i % 2 === 0 ? "bg-lime-500/10 border-lime-500/30" : "bg-teal-500/10 border-teal-500/30"
                      }`}>
                        <testimonial.icon className={`w-5 h-5 ${
                          i % 2 === 0 ? "text-lime-400" : "text-teal-400"
                        }`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[var(--m-heading)]">{testimonial.author}</p>
                        <p className="text-xs text-[var(--m-subtle)]">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works - 6-Pillar Flywheel Teaser */}
        <div className="bg-gradient-to-b from-[var(--m-bg-gradient-to)] via-[var(--m-bg-gradient-via)] to-[var(--m-bg-gradient-to)] py-12 md:py-20">
        <section id="flywheel" className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 md:mb-6 text-[var(--m-heading)]">
              The Growth Flywheel
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-[var(--m-muted)] leading-relaxed max-w-3xl">
              6 systems that feed each other. Every cycle builds momentum — automatically.
            </p>
          </div>

          {/* 6-pillar flywheel cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            {[
              {
                step: "01",
                title: "Website Attracts",
                desc: "Your SEO-optimized, conversion-focused website turns search traffic into phone calls and booking requests around the clock.",
                icon: Globe,
                color: "teal" as const,
                metric: "SEO",
                label: "optimized",
              },
              {
                step: "02",
                title: "AI Phone Captures",
                desc: "AI answers every call in <2 seconds with your company name, qualifies the inquiry, and books appointments directly into your calendar.",
                icon: Phone,
                color: "lime" as const,
                metric: "100%",
                label: "answered",
              },
              {
                step: "03",
                title: "Calendar Books",
                desc: "Real-time calendar sync ensures zero double-bookings. Customer gets instant SMS confirmation. Tech sees the job in their app.",
                icon: Calendar,
                color: "teal" as const,
                metric: "Zero",
                label: "conflicts",
              },
              {
                step: "04",
                title: "Service Delivers",
                desc: "Tech dispatched with smart routing, customer notified with name and ETA. Job notes captured via voice-to-text. Professional from start to finish.",
                icon: Wrench,
                color: "lime" as const,
                metric: "Auto",
                label: "ETA sent",
              },
              {
                step: "05",
                title: "Reviews Compound",
                desc: "After every job, automated follow-up requests 5-star Google reviews. AI generates professional responses. Your reputation grows on autopilot.",
                icon: Star,
                color: "teal" as const,
                metric: "5-Star",
                label: "automated",
              },
              {
                step: "06",
                title: "SEO Dominates",
                desc: "More reviews + managed Google Business Profile + citation building = higher local rankings. More people find you. The flywheel spins faster.",
                icon: MapPin,
                color: "lime" as const,
                metric: "#1",
                label: "local rank",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`group relative p-6 bg-gradient-to-br from-[var(--m-card)] to-[var(--m-card-alt)] border-2 transition-all hover:-translate-y-1 ${
                  item.color === "lime"
                    ? "border-lime-500/30 hover:border-lime-500/50 shadow-lg shadow-lime-500/10"
                    : "border-teal-500/30 hover:border-teal-500/50 shadow-lg shadow-teal-500/10"
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <div className="relative z-10">
                  {/* Step number - oversized background */}
                  <div
                    className={`text-7xl font-black absolute -top-2 -right-2 hidden md:block ${
                      item.color === "lime" ? "text-lime-500/[0.07]" : "text-teal-500/[0.07]"
                    }`}
                  >
                    {item.step}
                  </div>

                  {/* Icon */}
                  <div className={`inline-flex p-2.5 mb-4 border-2 ${
                    item.color === "lime"
                      ? "bg-lime-500/15 border-lime-500/40"
                      : "bg-teal-500/15 border-teal-500/40"
                  }`}>
                    <item.icon
                      className={`w-5 h-5 ${
                        item.color === "lime" ? "text-lime-400" : "text-teal-400"
                      }`}
                      strokeWidth={2}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-[var(--m-heading)] mb-2">{item.title}</h3>

                  {/* Description */}
                  <p className="text-xs text-[var(--m-muted)] leading-relaxed mb-4">{item.desc}</p>

                  {/* Metric */}
                  <div className={`inline-flex items-baseline gap-1.5 px-3 py-1.5 border ${
                    item.color === "lime"
                      ? "bg-lime-500/5 border-lime-500/20"
                      : "bg-teal-500/5 border-teal-500/20"
                  }`}>
                    <span className={`text-xl font-black ${
                      item.color === "lime" ? "text-lime-400" : "text-teal-400"
                    }`}>
                      {item.metric}
                    </span>
                    <span className="text-xs text-[var(--m-subtle)]">{item.label}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Flywheel loop indicator */}
          <motion.div
            className="flex items-center justify-center gap-3 mb-8 md:mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-lime-500/30" />
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--m-card)] border border-[var(--m-border-strong)]">
              <ArrowRight className="w-3 h-3 text-lime-400/60 rotate-[135deg]" />
              <span className="text-xs text-[var(--m-muted)]">Then the cycle repeats — compounding growth</span>
              <ArrowRight className="w-3 h-3 text-[var(--m-teal-text)] -rotate-45" />
            </div>
            <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-teal-500/30" />
          </motion.div>

          {/* CTA to full details */}
          <div className="text-center">
            <Link to="/how-it-works">
              <Button className="px-10 py-5 bg-gradient-to-r from-teal-500 to-teal-400 text-[#1f1f1f] font-black text-lg hover:from-teal-400 hover:to-teal-300 inline-flex items-center gap-3 shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 hover:scale-105 transition-all">
                See The Full Flywheel
                <ArrowRight className="w-6 h-6" />
              </Button>
            </Link>
            <p className="text-sm text-[var(--m-subtle)] mt-4">
              Website → Phone → Calendar → Service → Reviews → SEO → More Traffic
            </p>
          </div>

          {/* Value highlight - Platform capabilities */}
          <div
            className="mt-20 p-12 bg-gradient-to-br from-lime-500/10 via-lime-500/5 to-teal-500/10 border-2 border-lime-500/40 relative overflow-hidden"
            style={{
              boxShadow: "0 0 60px rgba(132, 204, 22, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.4)",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-lime-500/[0.05] via-transparent to-teal-500/[0.03] pointer-events-none" />

            <div className="relative z-10 text-center">
              <h3 className="text-4xl md:text-5xl font-black text-[var(--m-heading)] mb-6 tracking-tight">
                Stop Juggling Tools.{" "}
                <span className="bg-gradient-to-r from-lime-400 to-teal-400 bg-clip-text text-transparent">
                  Start Growing.
                </span>
              </h3>
              <p className="text-base sm:text-lg md:text-xl text-[var(--m-body)] max-w-3xl mx-auto mb-8 md:mb-10 leading-relaxed">
                Most service businesses use 5+ disconnected tools. Myna replaces them all
                with one integrated platform that actually talks to itself.
              </p>

              {/* Platform metrics */}
              <div className="inline-grid grid-cols-3 gap-6">
                {[
                  { value: "6", label: "Systems", sub: "Fully Integrated" },
                  { value: "$5,500+", label: "Replaced", sub: "In Separate Tools" },
                  { value: "48hrs", label: "Go Live", sub: "Not Months" },
                ].map((m, j) => (
                  <div key={j} className="text-center">
                    <div
                      className="text-2xl sm:text-3xl md:text-5xl font-black mb-1 leading-none"
                      style={{
                        background: "linear-gradient(135deg, #84cc16 0%, #a3e635 50%, #14b8a6 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      {m.value}
                    </div>
                    <div className="text-sm text-[var(--m-body)] font-semibold">{m.label}</div>
                    <div className="text-xs text-[var(--m-subtle)]">{m.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        </div>

        {/* Industry Solutions - Visual Cards */}
        <section className="bg-gradient-to-b from-[var(--m-bg)] to-[var(--m-deep)] py-14 md:py-24 relative overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />

          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-500/10 border border-teal-500/30 mb-6">
                  <ThermometerSun className="w-4 h-4 text-teal-400" />
                  <span className="text-xs font-bold text-teal-400 tracking-wider uppercase">Industry Specialists</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-4 md:mb-6 text-[var(--m-heading)] leading-tight">
                  Built for{" "}
                  <span className="bg-gradient-to-r from-teal-400 to-lime-400 bg-clip-text text-transparent">
                    Service Businesses
                  </span>
                </h2>
                <p className="text-base md:text-lg text-[var(--m-muted)] leading-relaxed mb-6 md:mb-8">
                  Our AI is pre-trained on industry terminology, common scenarios, and professional service language.
                  It speaks your customers' language from day one.
                </p>

                {/* Quick stats row */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { value: "48hr", label: "Go-live" },
                    { value: "Zero", label: "Missed calls" },
                    { value: "24/7", label: "Coverage" },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 bg-[var(--m-card)]/5 border border-[var(--m-border)]">
                      <div className="text-2xl font-black text-teal-400 mb-1">{stat.value}</div>
                      <div className="text-xs text-[var(--m-subtle)] uppercase tracking-wider">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <Link to="/solutions">
                  <Button className="px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-400 text-[#1f1f1f] font-black text-base hover:from-teal-400 hover:to-teal-300 inline-flex items-center gap-2 shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/35 hover:scale-105 transition-all">
                    Explore Solutions
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>

              {/* Right: Industry Cards */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    icon: ThermometerSun,
                    title: "HVAC",
                    desc: "Heating, cooling, ventilation specialists",
                    color: "lime",
                    delay: 0
                  },
                  {
                    icon: Droplets,
                    title: "Plumbing",
                    desc: "Emergency & residential plumbers",
                    color: "teal",
                    delay: 0.1
                  },
                  {
                    icon: Zap,
                    title: "Electrical",
                    desc: "Licensed electrical contractors",
                    color: "teal",
                    delay: 0.2
                  },
                  {
                    icon: Wrench,
                    title: "General Trades",
                    desc: "Home service professionals",
                    color: "lime",
                    delay: 0.3
                  },
                ].map((industry, i) => (
                  <motion.div
                    key={i}
                    className={`group p-6 border-2 transition-all hover:-translate-y-2 ${
                      industry.color === "lime"
                        ? "bg-gradient-to-br from-lime-500/5 to-transparent border-lime-500/20 hover:border-lime-500/40 hover:shadow-xl hover:shadow-lime-500/10"
                        : "bg-gradient-to-br from-teal-500/5 to-transparent border-teal-500/20 hover:border-teal-500/40 hover:shadow-xl hover:shadow-teal-500/10"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: industry.delay }}
                  >
                    <div className={`inline-flex p-3 mb-4 border ${
                      industry.color === "lime"
                        ? "bg-lime-500/10 border-lime-500/30"
                        : "bg-teal-500/10 border-teal-500/30"
                    }`}>
                      <industry.icon className={`w-6 h-6 ${
                        industry.color === "lime" ? "text-lime-400" : "text-teal-400"
                      } group-hover:scale-110 transition-transform`} />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-[var(--m-heading)] mb-2">{industry.title}</h3>
                    <p className="text-sm text-[var(--m-subtle)]">{industry.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══ CONTENT & MARKETING INTELLIGENCE ═══ */}
        <section className="py-14 md:py-24 relative overflow-hidden">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            {/* Section header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10 md:mb-16"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-lime-500/10 border border-lime-500/30 mb-6">
                <Sparkles className="w-4 h-4 text-lime-400" />
                <span className="text-xs font-bold text-lime-400 tracking-wider uppercase">Content & Marketing</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-4 md:mb-6 text-[var(--m-heading)] leading-tight">
                Create Content With{" "}
                <span className="bg-gradient-to-r from-lime-400 to-teal-400 bg-clip-text text-transparent">
                  Your Voice
                </span>
              </h2>
              <p className="text-base md:text-lg text-[var(--m-muted)] leading-relaxed max-w-3xl mx-auto">
                Social media posts, blog articles, and marketing materials — generated by AI that understands your industry, your brand, and your market.
              </p>
            </motion.div>

            {/* Social Studio + Voice Command Centre — two-column layout */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">

              {/* ── SOCIAL STUDIO ── */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-sm bg-lime-500/10 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-lime-400" />
                  </div>
                  <p className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-lime-400/70">Social Studio</p>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-[var(--m-heading)] mb-3 tracking-tight">
                  Industry-smart content in seconds
                </h3>
                <p className="text-sm text-[var(--m-muted)] leading-relaxed mb-6">
                  AI that knows your industry generates branded posts for Instagram, Facebook, LinkedIn, and Google — with charts, carousels, and captions ready to publish.
                </p>

                {/* Content card mockups */}
                <div className="space-y-3 mb-6">
                  {[
                    { title: "Emergency HVAC: What to Do Before We Arrive", category: "Education", platforms: "Instagram · Facebook", status: "Ready", color: "lime" },
                    { title: "Spring AC Tune-Up Checklist", category: "Seasonal", platforms: "All Platforms", status: "Scheduled", color: "teal" },
                    { title: "Why Your Water Heater is Making That Noise", category: "FAQ", platforms: "LinkedIn · Google", status: "Draft", color: "gray" },
                  ].map((card, i) => (
                    <motion.div
                      key={card.title}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.3 + i * 0.12 }}
                      className="p-3.5 rounded-sm border border-[var(--m-border)] bg-[var(--m-card)]/40 flex items-center gap-3 group hover:border-lime-500/20 transition-all"
                    >
                      <div className={`w-1.5 h-10 rounded-full shrink-0 ${card.color === "lime" ? "bg-lime-500" : card.color === "teal" ? "bg-teal-500" : "bg-[#6b7280]"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--m-text-primary)] truncate">{card.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-[#6b7280]">{card.category}</span>
                          <span className="text-[#6b7280]">&middot;</span>
                          <span className="text-[10px] text-[#6b7280]">{card.platforms}</span>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${card.color === "lime" ? "text-lime-400" : card.color === "teal" ? "text-teal-400" : "text-[#6b7280]"}`}>
                        {card.status}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* Feature bullets */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "Industry-trained AI",
                    "Multi-platform publishing",
                    "Branded charts & carousels",
                    "One-click scheduling",
                  ].map((feat, i) => (
                    <motion.div
                      key={feat}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 + i * 0.08 }}
                      className="flex items-center gap-2"
                    >
                      <Check className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                      <span className="text-xs text-[var(--m-muted)]">{feat}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* ── VOICE COMMAND CENTRE ── */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="relative"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-sm bg-teal-500/10 flex items-center justify-center">
                    <Volume2 className="w-4 h-4 text-teal-400" />
                  </div>
                  <p className="text-xs font-mono font-bold tracking-[0.2em] uppercase text-teal-400/70">Voice Command Centre</p>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-[var(--m-heading)] mb-3 tracking-tight">
                  Say it. <span className="bg-gradient-to-r from-teal-400 to-lime-400 bg-clip-text text-transparent">Myna builds it.</span>
                </h3>
                <p className="text-sm text-[var(--m-muted)] leading-relaxed mb-6">
                  Speak naturally and watch Myna generate content options with visuals, captions, and platform-specific formatting. Review, refine, and publish — all with your voice.
                </p>

                {/* Voice orb visualization */}
                <div className="flex justify-center mb-8">
                  <motion.div className="relative w-40 h-40 sm:w-48 sm:h-48">
                    {/* Rotating ring */}
                    <motion.div
                      className="absolute rounded-full border border-dashed border-teal-500/20"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      style={{ width: "130%", height: "130%", top: "-15%", left: "-15%" }}
                    />
                    {/* Breathing glow */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      animate={{ scale: [1, 1.12, 1], opacity: [0.25, 0.45, 0.25] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      style={{ background: "radial-gradient(circle, rgba(45,212,191,0.15) 0%, transparent 70%)" }}
                    />
                    {/* Orb */}
                    <motion.div
                      className="absolute inset-4 sm:inset-5 rounded-full flex items-center justify-center"
                      animate={{ scale: [1, 1.03, 1] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                      style={{ background: "conic-gradient(from 0deg, #2dd4bf, #a3e635, #2dd4bf)" }}
                    >
                      <div className="w-full h-full rounded-full bg-[var(--m-bg)] m-[2px] flex items-center justify-center">
                        <Volume2 className="w-7 h-7 text-teal-400" />
                      </div>
                    </motion.div>
                    {/* Waveform */}
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-end gap-1">
                      {[0.6, 0.9, 0.4, 1, 0.7, 0.5, 0.8].map((h, i) => (
                        <motion.div
                          key={i}
                          className="w-1 rounded-full bg-teal-400/50"
                          animate={{ height: [h * 10, h * 22, h * 10] }}
                          transition={{ duration: 0.8 + i * 0.1, repeat: Infinity, ease: "easeInOut", delay: i * 0.08 }}
                        />
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Workflow steps */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { step: "01", label: "Speak", desc: "Tell Myna what to create" },
                    { step: "02", label: "Review", desc: "AI generates 3 options" },
                    { step: "03", label: "Publish", desc: "Go live everywhere" },
                  ].map((s, i) => (
                    <motion.div
                      key={s.step}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.12 }}
                      className="text-center p-3 border border-[var(--m-border)] rounded-sm bg-[var(--m-card)]/30"
                    >
                      <span className="text-[10px] font-mono font-bold text-teal-400/40">{s.step}</span>
                      <p className="text-sm font-semibold text-[var(--m-text-primary)] mt-1">{s.label}</p>
                      <p className="text-[10px] text-[#6b7280] mt-0.5">{s.desc}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Feature bullets */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "Voice-to-content in 30 seconds",
                    "No typing required",
                    "Pulls live business data",
                    "Refine by speaking",
                  ].map((feat, i) => (
                    <motion.div
                      key={feat}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 + i * 0.08 }}
                      className="flex items-center gap-2"
                    >
                      <Check className="w-3.5 h-3.5 text-teal-400 shrink-0" />
                      <span className="text-xs text-[var(--m-muted)]">{feat}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Myna - Differentiators */}
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-14 md:py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--m-badge-bg)] border border-[var(--m-ring-border)] mb-6">
                <Award className="w-4 h-4 text-[var(--m-lime)]" />
                <span className="text-xs font-bold text-[var(--m-lime)] tracking-wider uppercase">The Myna Difference</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-4 md:mb-6 text-[var(--m-heading)] leading-tight">
                Built Different.{" "}
                <span className="bg-gradient-to-r from-lime-400 to-teal-400 bg-clip-text text-transparent">
                  Built Better.
                </span>
              </h2>
              <p className="text-base md:text-lg text-[var(--m-muted)] leading-relaxed mb-6 md:mb-8">
                We're not a generic AI tool. Myna is an intelligent business systems platform purpose-built
                for service companies who want to capture every lead, build reputation, and dominate local search.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  {
                    title: "Industry-Trained AI",
                    desc: "Pre-trained on service industry terminology. Sounds like your best receptionist from day one.",
                  },
                  {
                    title: "Real Calendar Integration",
                    desc: "Direct Google Calendar sync. Appointments booked instantly, no double-bookings, automatic confirmations.",
                  },
                  {
                    title: "North American Data Security",
                    desc: "Your data stays in North America. Secure data centers with full compliance for US & Canadian businesses.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className={`mt-1 w-6 h-6 flex items-center justify-center shrink-0 ${
                      i % 2 === 0 ? "bg-lime-500/20" : "bg-teal-500/20"
                    }`}>
                      <CheckCircle2 className={`w-4 h-4 ${
                        i % 2 === 0 ? "text-lime-400" : "text-teal-400"
                      }`} />
                    </div>
                    <div>
                      <h4 className="text-[var(--m-heading)] font-semibold mb-1">{item.title}</h4>
                      <p className="text-sm text-[var(--m-subtle)]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/demo">
                <Button className="px-8 py-4 bg-gradient-to-r from-lime-500 to-lime-400 text-[#1f1f1f] font-black text-base hover:from-lime-600 hover:to-lime-500 inline-flex items-center gap-2 shadow-lg shadow-lime-500/25 hover:shadow-xl hover:shadow-lime-500/35 hover:scale-105 transition-all">
                  Experience the Difference
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            {/* Right: Visual Stats */}
            <motion.div
              className="grid grid-cols-2 gap-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                {
                  value: "48",
                  unit: "hours",
                  label: "Average go-live time",
                  desc: "From signup to answering calls",
                  color: "lime",
                },
                {
                  value: "100",
                  unit: "%",
                  label: "Calls answered",
                  desc: "24/7, even at 2 AM",
                  color: "teal",
                },
                {
                  value: "<2",
                  unit: "sec",
                  label: "Answer time",
                  desc: "Faster than any human",
                  color: "teal",
                },
                {
                  value: "0",
                  unit: "",
                  label: "Setup complexity",
                  desc: "We handle everything",
                  color: "lime",
                },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className={`p-8 border-2 ${
                    stat.color === "lime"
                      ? "bg-gradient-to-br from-lime-500/10 to-lime-500/5 border-lime-500/30"
                      : "bg-gradient-to-br from-teal-500/10 to-teal-500/5 border-teal-500/30"
                  }`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * i }}
                >
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className={`text-3xl sm:text-5xl font-black ${
                      stat.color === "lime" ? "text-lime-400" : "text-teal-400"
                    }`}>{stat.value}</span>
                    {stat.unit && (
                      <span className={`text-xl font-bold ${
                        stat.color === "lime" ? "text-lime-400/70" : "text-teal-400/70"
                      }`}>{stat.unit}</span>
                    )}
                  </div>
                  <p className="text-[var(--m-heading)] font-semibold mb-1">{stat.label}</p>
                  <p className="text-xs text-[var(--m-subtle)]">{stat.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Final CTA - Distinctive Split Layout */}
        <section className="bg-gradient-to-b from-[var(--m-bg)] to-[var(--m-deep)] py-24 md:py-32 relative overflow-hidden">
          {/* Animated gradient orbs */}
          <motion.div
            className="absolute top-0 right-0 w-[600px] h-[600px] opacity-30"
            style={{
              background: "radial-gradient(circle, rgba(132,204,22,0.15) 0%, transparent 60%)",
              filter: "blur(100px)",
            }}
            animate={{
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-0 left-0 w-[500px] h-[500px] opacity-20"
            style={{
              background: "radial-gradient(circle, rgba(20,184,166,0.2) 0%, transparent 60%)",
              filter: "blur(80px)",
            }}
            animate={{
              x: [0, -40, 0],
              y: [0, 40, 0],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: Compelling Copy */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black mb-6 md:mb-8 text-[var(--m-heading)] leading-[1.05]">
                  Ready to{" "}
                  <span className="bg-gradient-to-r from-lime-400 via-lime-300 to-teal-400 bg-clip-text text-transparent">
                    transform
                  </span>{" "}
                  your business?
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-[var(--m-muted)] leading-relaxed mb-8 md:mb-10">
                  Join service businesses across America who never miss a call.
                  Try our live demo—no signup required—and hear the difference.
                </p>

                {/* Trust signals */}
                <div className="flex flex-wrap gap-6 mb-10">
                  {[
                    { icon: Shield, text: "Secure Data" },
                    { icon: Award, text: "North American Servers" },
                    { icon: Headphones, text: "Local Support" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-[var(--m-subtle)]">
                      <item.icon className="w-4 h-4 text-teal-400" />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/request-demo">
                    <Button className="px-10 py-6 bg-gradient-to-r from-lime-500 to-lime-400 text-[#1f1f1f] font-black text-lg hover:from-lime-600 hover:to-lime-500 inline-flex items-center gap-3 shadow-2xl shadow-lime-500/30 hover:shadow-lime-500/50 hover:scale-105 transition-all">
                      <Calendar className="w-6 h-6" />
                      Book a Demo
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="outline" className="px-10 py-6 bg-[var(--m-card)]/5 border-2 border-[var(--m-border-strong)] text-[var(--m-heading)] font-bold hover:bg-[var(--m-card)]/10 hover:border-[var(--m-border-strong)] transition-all">
                      View Pricing
                    </Button>
                  </Link>
                </div>
              </motion.div>

              {/* Right: Visual Element - Stylized Phone/Call Animation */}
              <motion.div
                className="relative flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="relative">
                  {/* Glowing ring */}
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      width: 280,
                      height: 280,
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      background: "conic-gradient(from 0deg, rgba(132,204,22,0.3), rgba(20,184,166,0.3), rgba(132,204,22,0.3))",
                      filter: "blur(40px)",
                    }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  />

                  {/* Main circle */}
                  <div
                    className="relative w-[200px] h-[200px] rounded-full flex items-center justify-center border-2 border-lime-500/30"
                    style={{
                      background: "linear-gradient(135deg, #2e2e2e 0%, #252525 100%)",
                      boxShadow: "0 0 80px rgba(132, 204, 22, 0.2), inset 0 0 40px rgba(0,0,0,0.5)",
                    }}
                  >
                    {/* Pulsing rings */}
                    {[1, 2, 3].map((ring) => (
                      <motion.div
                        key={ring}
                        className="absolute inset-0 rounded-full border border-lime-500/20"
                        animate={{
                          scale: [1, 1.5 + ring * 0.3, 1],
                          opacity: [0.3, 0, 0.3],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: ring * 0.5,
                          ease: "easeOut",
                        }}
                      />
                    ))}

                    {/* Phone icon */}
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <Phone className="w-16 h-16 text-lime-400" strokeWidth={1.5} />
                    </motion.div>
                  </div>

                  {/* Floating stats around the circle */}
                  {[
                    { value: "100%", label: "Answered", angle: -45, color: "lime" },
                    { value: "<2s", label: "Response", angle: 45, color: "teal" },
                    { value: "24/7", label: "Coverage", angle: 135, color: "lime" },
                    { value: "Zero", label: "Missed", angle: 225, color: "teal" },
                  ].map((stat, i) => {
                    const radius = 170;
                    const x = Math.cos((stat.angle * Math.PI) / 180) * radius;
                    const y = Math.sin((stat.angle * Math.PI) / 180) * radius;
                    return (
                      <motion.div
                        key={i}
                        className={`absolute px-3 py-2 border ${
                          stat.color === "lime"
                            ? "bg-lime-500/10 border-lime-500/30"
                            : "bg-teal-500/10 border-teal-500/30"
                        }`}
                        style={{
                          left: `calc(50% + ${x}px)`,
                          top: `calc(50% + ${y}px)`,
                          transform: "translate(-50%, -50%)",
                        }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                      >
                        <div className={`text-lg font-black ${
                          stat.color === "lime" ? "text-lime-400" : "text-teal-400"
                        }`}>{stat.value}</div>
                        <div className="text-[10px] text-[var(--m-subtle)] uppercase tracking-wider">{stat.label}</div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
